import { RouteComponentProps } from '@reach/router';
import { LessonsApi, SchemeOfWork } from 'api/lessonsApi';
import LessonPageStatus from 'components/lessons/LessonPageStatus';
import { LessonPrintParts, useLessonPrint } from 'components/lessons/LessonPrint';
import {
  LessonsRouteParams,
  splitLines,
  unitPath,
  useLessonsPage,
} from 'components/lessons/lessonsHelpers';
import LessonTop from 'components/lessons/LessonTop';
import Page from 'components/shared/Page';
import React from 'react';

type Props = RouteComponentProps<LessonsRouteParams>;

const LessonSchemeOfWork: React.FC<Props> = (props) => {
  const result = useLessonsPage<SchemeOfWork>(props, (query) =>
    LessonsApi.getSchemeOfWork(query)
  );
  const subjectsId = result && typeof result !== 'string' ? result.subject.subjectsID : null;
  const { print, popup, errorMessage } = useLessonPrint(subjectsId);

  if (result === null || typeof result === 'string') {
    return <LessonPageStatus status={result} />;
  }

  const { subject, query, details: scheme } = result;
  const details = scheme.schemeOfWork;
  const assessmentYears = Array.from(
    new Set(scheme.assessmentCriterias.map((item) => item.yearNames))
  );

  return (
    <Page bypassPageAccess>
      <div className="LessonPage">
        <LessonPrintParts schoolName={scheme.schoolName} />
        <LessonTop
          activity={scheme.activity}
          keyStageName={scheme.keyStageName}
          backPath={`${unitPath(
            subject.internalCode,
            scheme.keyStage.strippedName,
            scheme.activity.strippedName
          )}?years=${query.years}`}
          subtitle="Unit of Work"
          onPrint={print}
        >
          {errorMessage}
        </LessonTop>
        <div className="WidthContent LessonSection">
          <h2>Introduction</h2>
          {splitLines(details.introduction).map((line, index) => (
            <p key={index}>{line}</p>
          ))}
        </div>
        <div className="WidthContent LessonSection">
          <h2>Key Skills</h2>
          <ul>
            {splitLines(details.keyVocabulary).map((line, index) => (
              <li key={index}>{line}</li>
            ))}
          </ul>
        </div>
        {scheme.lessons.length > 0 && (
          <div className="WidthContent LessonSection">
            <h2>Learning Objective</h2>
            <table className="LessonTable">
              <tbody>
                {scheme.lessons.map((lesson) => (
                  <tr key={lesson.lessonsId}>
                    <td className="LessonTableLabel">{`LESSON ${lesson.lessonNumber}`}</td>
                    <td>
                      {lesson.learningObjectives.map((item, index) => (
                        <div key={index}>{item.objective}</div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {scheme.assessmentCriterias.length > 0 && (
          <div className="WidthContent LessonSection">
            <h2>Assessment Criteria</h2>
            <div className="LessonColumns">
              {assessmentYears.map((yearNames) => (
                <div key={yearNames} className="LessonColumn">
                  <div className="LessonTableLabel">{yearNames}</div>
                  <table className="LessonTable">
                    <tbody>
                      {scheme.assessmentCriterias
                        .filter((item) => item.yearNames === yearNames)
                        .map((item) => (
                          <tr key={item.assessmentSuccessCriteriasId}>
                            <td>{item.criteria}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          </div>
        )}
        {scheme.nationalCurriculums.length > 0 && (
          <div className="WidthContent LessonSection">
            <h2>
              {scheme.keyStage.name.toLowerCase() === 'eyfs'
                ? 'Links to the Early Learning Goals'
                : 'Links to the National Curriculum'}
            </h2>
            <div className="LessonColumns">
              {scheme.nationalCurriculums.map((item) => (
                <div key={item.nationalCurriculumsId} className="LessonColumn">
                  <div className="LessonTableLabel">{item.nationalCurriculumArea}</div>
                  <table className="LessonTable">
                    <tbody>
                      {item.learningAreas.map((area, index) => (
                        <tr key={index}>
                          <td>{area.specificLearningArea}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          </div>
        )}
        {details.healthSafety && (
          <div className="WidthContent LessonSection">
            <h2>Healthy Participation</h2>
            {splitLines(details.healthSafety).map((line, index) => (
              <p key={index}>{line}</p>
            ))}
          </div>
        )}
      </div>
      {popup}
    </Page>
  );
};

export default LessonSchemeOfWork;
